import {
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { Button, Modal, Tag } from "antd";
import { FC, MouseEvent, useEffect, useState } from "react";
import { ProjectPhase } from "../../projectPhase";
import TaskPriorityTag from "../TaskPriorityTag";
import TaskCommentReplies from "../TaskCommentReplies";
import TaskSubtaskManager, {
  TaskSubtaskDraft,
  TaskSubtaskItem,
} from "../TaskSubtaskManager";
import "./index.css";

export interface TaskNodeDetailComment {
  comment_id: string;
  author: string;
  content: string;
  subtask_ids: string[];
  replies?: TaskNodeDetailReply[];
  created_at: number;
  updated_at: number;
}

export interface TaskNodeDetailReply {
  reply_id: string;
  author: string;
  content: string;
  created_at: number;
  updated_at: number;
}

export interface TaskNodeDetailTask {
  id?: string;
  project_id: string;
  phase: ProjectPhase;
  creator: string;
  assignees: string[];
  predecessor_ids: string[];
  title: string;
  content?: string;
  priority?: "low" | "medium" | "high" | "urgent";
  status: "todo" | "doing" | "blocked" | "review" | "done" | "discarded";
  due_time?: number;
  comments?: TaskNodeDetailComment[];
}

interface TaskNodeDetailModalProps {
  open: boolean;
  task: TaskNodeDetailTask | null;
  phaseLabel: string;
  statusText: string;
  assigneeNames: string[];
  dueTimeText: string;
  currentUserId: string;
  focusedContentItemId?: string;
  focusedSubtaskId?: string;
  canManage: boolean;
  canManageContentStatus?: boolean;
  canComment: boolean;
  getMemberName: (userId: string) => string;
  formatTime: (timestamp: number) => string;
  onClose: () => void;
  onEdit: (task: TaskNodeDetailTask) => void;
  onDelete: (task: TaskNodeDetailTask) => void;
  onAddComment: (task: TaskNodeDetailTask, subtaskId?: string) => void;
  onEditComment: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment
  ) => void;
  onDeleteComment: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment
  ) => void;
  onAddCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    content: string
  ) => Promise<boolean>;
  onUpdateCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply,
    content: string
  ) => Promise<boolean>;
  onDeleteCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply
  ) => Promise<boolean>;
  onContentClick: (event: MouseEvent<HTMLDivElement>) => void;
  subtasks: TaskSubtaskItem[];
  loadingSubtasks: boolean;
  onHandoffSubtask?: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onSubmitSubtaskReview: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onUpdateSubtask: (
    subtask: TaskSubtaskItem,
    draft: TaskSubtaskDraft
  ) => Promise<boolean>;
  onCompleteSubtask: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onApproveSubtask: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onRejectSubtask: (
    subtask: TaskSubtaskItem,
    reason: string
  ) => Promise<boolean>;
}

const TaskNodeDetailModal: FC<TaskNodeDetailModalProps> = ({
  open,
  task,
  phaseLabel,
  statusText,
  assigneeNames,
  dueTimeText,
  currentUserId,
  focusedContentItemId,
  focusedSubtaskId,
  canManage,
  canManageContentStatus = false,
  canComment,
  getMemberName,
  formatTime,
  onClose,
  onEdit,
  onDelete,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onAddCommentReply,
  onUpdateCommentReply,
  onDeleteCommentReply,
  onContentClick,
  subtasks,
  loadingSubtasks,
  onHandoffSubtask = async () => false,
  onSubmitSubtaskReview,
  onUpdateSubtask,
  onCompleteSubtask,
  onApproveSubtask,
  onRejectSubtask,
}) => {
  const [selectedSubtaskId, setSelectedSubtaskId] = useState("");
  const taskIdentity = task?.id || task?.title || "";

  useEffect(() => {
    if (!open || !taskIdentity) return;
    setSelectedSubtaskId(focusedSubtaskId || "");
  }, [focusedSubtaskId, open, taskIdentity]);

  if (!task) {
    return null;
  }

  const selectedSubtask =
    subtasks.find((subtask) => subtask.id === selectedSubtaskId) || null;
  const allComments = [...(task.comments || [])].sort(
    (prev, next) => next.created_at - prev.created_at
  );
  const comments = allComments.filter((comment) =>
    selectedSubtaskId
      ? (comment.subtask_ids || []).includes(selectedSubtaskId)
      : !(comment.subtask_ids || []).length
  );

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      centered
      className="taskNodeDetailModal"
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={onClose}
      open={open}
      title={null}
      width={1380}
    >
      <div className="taskNodeDetailModal__shell">
        <header className="taskNodeDetailModal__header">
          <div className="taskNodeDetailModal__identity">
            <div className="taskNodeDetailModal__eyebrow">
              <Tag className="taskNodeDetailModal__phase">{phaseLabel}</Tag>
              <Tag
                className={`taskNodeDetailModal__status taskNodeDetailModal__status--${task.status}`}
              >
                {statusText}
              </Tag>
              <TaskPriorityTag priority={task.priority || "medium"} />
            </div>
            <h2>{task.title}</h2>
            <div className="taskNodeDetailModal__meta">
              <div className="taskNodeDetailModal__assignees">
                <small>负责人</small>
                {assigneeNames.length ? (
                  assigneeNames.map((name, index) => (
                    <Tag key={`${task.assignees[index]}-${name}`} title={name}>
                      {name}
                    </Tag>
                  ))
                ) : (
                  <Tag>未设置</Tag>
                )}
              </div>
              <span>预计完成：{dueTimeText || "未设置"}</span>
              <span>批注：{allComments.length} 条</span>
            </div>
          </div>
          <div className="taskNodeDetailModal__actions">
            {canManage && (
              <>
              <Button
                aria-label="编辑节点"
                className="taskNodeDetailModal__actionButton taskNodeDetailModal__actionButton--edit"
                icon={<EditOutlined />}
                onClick={() => onEdit(task)}
              >
                编辑
              </Button>
              <Button
                aria-label="删除节点"
                className="taskNodeDetailModal__actionButton taskNodeDetailModal__actionButton--delete"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(task)}
              >
                删除
              </Button>
              </>
            )}
            <Button
              aria-label="关闭节点详情"
              className="taskNodeDetailModal__closeButton"
              icon={<CloseOutlined />}
              onClick={onClose}
              title="关闭节点详情"
            />
          </div>
        </header>

        <div className="taskNodeDetailModal__body">
          <section className="taskNodeDetailModal__contentPane">
            <div
              className="taskNodeDetailModal__subtaskPanel"
              data-testid="task-detail-subtask-panel"
            >
              <TaskSubtaskManager
                canManage={false}
                canManageContentStatus={canManageContentStatus}
                currentUserId={currentUserId}
                focusedContentItemId={focusedContentItemId}
                focusedSubtaskId={focusedSubtaskId}
                getMemberName={getMemberName}
                layoutMode="detail"
                loading={loadingSubtasks}
                memberOptions={[]}
                onContentClick={onContentClick}
                onCreate={async () => false}
                onDelete={async () => false}
                onHandoff={onHandoffSubtask}
                onComplete={onCompleteSubtask}
                onApprove={onApproveSubtask}
                onReject={onRejectSubtask}
                onSelectedSubtaskChange={setSelectedSubtaskId}
                onSubmitReview={onSubmitSubtaskReview}
                onUpdate={onUpdateSubtask}
                selectedSubtaskId={selectedSubtaskId}
                subtasks={subtasks}
              />
            </div>
          </section>

          <aside className="taskNodeDetailModal__commentPane">
            <div className="taskNodeDetailModal__sectionTitle">
              <div>
                <span>COLLABORATION</span>
                <h3>
                  {selectedSubtask
                    ? `子任务批注 · ${selectedSubtask.title}`
                    : "任务模块批注"}
                </h3>
                <small>
                  {selectedSubtask
                    ? `${comments.length} 条关联批注`
                    : `${comments.length} 条整体批注`}
                </small>
              </div>
              {canComment && (
                <Button
                  aria-label="新增批注"
                  className="taskNodeDetailModal__addCommentButton"
                  icon={<CommentOutlined />}
                  onClick={() =>
                    selectedSubtaskId
                      ? onAddComment(task, selectedSubtaskId)
                      : onAddComment(task)
                  }
                  type="primary"
                >
                  新增批注
                </Button>
              )}
            </div>

            <div className="taskNodeDetailModal__commentList">
              {comments.length ? (
                comments.map((comment) => (
                  <article
                    className="taskNodeDetailModal__comment"
                    key={comment.comment_id}
                  >
                    <div className="taskNodeDetailModal__commentHeader">
                      <div>
                        <strong>{getMemberName(comment.author)}</strong>
                        <span>
                          {formatTime(comment.updated_at || comment.created_at)}
                        </span>
                      </div>
                      {comment.author === currentUserId && canComment && (
                        <div className="taskNodeDetailModal__commentActions">
                          <Button
                            aria-label="编辑批注"
                            onClick={() => onEditComment(task, comment)}
                            size="small"
                            type="text"
                          >
                            编辑
                          </Button>
                          <Button
                            aria-label="删除批注"
                            danger
                            onClick={() => onDeleteComment(task, comment)}
                            size="small"
                            type="text"
                          >
                            删除
                          </Button>
                        </div>
                      )}
                    </div>
                    <div
                      className="taskNodeDetailModal__commentContent"
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                      onClick={onContentClick}
                    />
                    <TaskCommentReplies
                      canComment={canComment}
                      comment={comment}
                      currentUserId={currentUserId}
                      formatTime={formatTime}
                      getMemberName={getMemberName}
                      onAddReply={onAddCommentReply}
                      onDeleteReply={onDeleteCommentReply}
                      onUpdateReply={onUpdateCommentReply}
                      task={task}
                    />
                  </article>
                ))
              ) : (
                <div className="taskNodeDetailModal__empty">
                  <CommentOutlined />
                  <strong>
                    {selectedSubtask
                      ? "该子任务暂无关联批注"
                      : "该任务模块暂无整体批注"}
                  </strong>
                  <span>
                    {selectedSubtask
                      ? "可新增一条仅关联当前子任务的批注"
                      : "选择左侧子任务可查看其关联批注"}
                  </span>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
};

export default TaskNodeDetailModal;
